import { FC, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

import { DialogContent, DialogClose } from '@app/shared/ui/dialog';
import { CrossIcon, DiamondIcon, ErrorCircleIcon } from '@app/components/ui/icons';
import { Button } from '@app/shared/ui/button';
import { cvaIcon } from '@app/shared/ui/elements';
import { useTranslation } from '@app/app/i18n';
import { StandardTooltip } from '@app/shared/ui/standard-tooltip';
import { TooltipProvider } from '@app/shared/ui/tooltip';

import { useGetUserInitQuery } from '@app/store/api/user.api';
import { useAppSelector } from '@app/hooks/use-store';

import {
  cvaCardContainer,
  cvaInnerContainer,
  cvaTitleContainer,
  cvaTitle,
  cvaSubtitle,
  cvaImageContainer,
  cvaImageBackground,
  cvaPurpleGradient,
  cvaContentSection,
  cvaRenewalText,
  cvaCancelButton,
  cvaTextarea,
  cvaButtonsContainer,
  cvaSendButton,
  cvaLeaveButton,
  cvaGradientBottom,
  cvaGradientTop,
  cvaConfettiImage,
  cvaWonderslideImage,
} from './payments-pro-styles';

type PaymentsProProps = {
  onCancel: () => void;
};

export const PaymentsPro: FC<PaymentsProProps> = ({ onCancel }) => {
  const { isAuthenticated } = useAppSelector((state) => state.authState);
  const { data: userData } = useGetUserInitQuery(null, { skip: !isAuthenticated });
  const { t } = useTranslation('payments-dialog');

  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const productName = userData?.result?.product_balance?.product?.name ?? 'Pro';
  const expirationDate = userData?.result?.product_balance?.expire;

  const actualTokensCount = useMemo(() => {
    if (!userData?.result?.product_balance) return undefined;
    const { included_ai_token_count = 0, purchased_ai_token_count = 0 } =
      userData.result.product_balance;
    return included_ai_token_count + purchased_ai_token_count;
  }, [userData]);

  const rawDate = expirationDate ? new Date(expirationDate) : null;
  const isValidDate = rawDate && !isNaN(rawDate.getTime());
  const formattedDate = isValidDate ? format(rawDate!, 'MMM d, yyyy') : '—';

  const handleCancelClick = () => {
    setShowCancelForm(true);
  };

  const handleSendCancel = () => {
    onCancel();
    setShowCancelForm(false);
  };

  const handleKeepSubscription = () => {
    setShowCancelForm(false);
    setCancelReason('');
  };

  return (
    <DialogContent
      className="max-w-[31.25rem] bg-transparents"
      customCloseIcon={
        <DialogClose
          aria-label={t('common.close')}
          className="absolute right-2 top-2 transition-colors disabled:pointer-events-none text-general_text_MII_secondary_i hover:text-general_text_MII_default focus-visible:outline"
        >
          <CrossIcon className={cvaIcon()} />
          <span className="sr-only">{t('common.close')}</span>
        </DialogClose>
      }
    >
      <div className={cvaCardContainer({ showCancelForm })}>
        <div className="flex flex-col items-center w-full">
          <div className={cvaInnerContainer()}>
            {/* Title */}
            <div className={cvaTitleContainer()}>
              <div className="flex items-center justify-between w-full">
                <h2 className={cvaTitle()}>{productName}</h2>
                {actualTokensCount !== undefined && (
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center gap-1.5 py-2 px-3 rounded-3xl bg-general_background_MIII_400_dark">
                      <span className="font-inter text-xs leading-[10px] font-normal text-general_MI_500_default">
                        AI tokens
                      </span>
                      <DiamondIcon className="w-5 h-5 text-general_MI_500_default" />
                      <span className="font-inter text-xs leading-[10px] font-normal text-general_MI_500_default">
                        {actualTokensCount}
                      </span>
                    </div>
                    <TooltipProvider>
                      <StandardTooltip
                        text={t('dialog.currentBalance', {
                          count: actualTokensCount,
                        })}
                        tooltipProps={{ delayDuration: 0 }}
                        contentProps={{ side: 'bottom' }}
                        contentClassName="bg-general_background_MIII_450_dark text-general_text_MII_secondary_i border-0"
                        arrowClassName="hidden"
                      >
                        <div className="cursor-help">
                          <ErrorCircleIcon className={cvaIcon()} />
                        </div>
                      </StandardTooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
              <p className={cvaSubtitle()}>{t('pro.subtitle')}</p>
            </div>

            {/* Image or Form - with transition */}
            <AnimatePresence mode="sync">
              {!showCancelForm && (
                <motion.div
                  key="image-container"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: '8.75rem' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className={cvaImageContainer()}
                >
                  <div className={cvaImageBackground()}>
                    {/* Purple background */}
                    <div role="presentation" aria-hidden="true" className={cvaPurpleGradient()} />

                    {/* Background confetti image */}
                    <img
                      role="presentation"
                      aria-hidden="true"
                      alt=""
                      className={cvaConfettiImage()}
                      src="/static/i/payments_dialog/big-conffeti.png"
                    />

                    {/* 3D character */}
                    <img
                      alt="Wonderslide"
                      className={cvaWonderslideImage()}
                      src="/static/i/payments_dialog/wonderslide.png"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Content section with transition */}
            <div className={cvaContentSection()}>
              <AnimatePresence mode="wait">
                {!showCancelForm ? (
                  <motion.div
                    key="cancel-info"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="w-full flex flex-col gap-3 items-center"
                  >
                    <p className={cvaRenewalText()}>
                      {t('pro.renewalText', { date: formattedDate })}
                    </p>
                    <Button
                      onClick={handleCancelClick}
                      className={cvaCancelButton()}
                      variant="ghost"
                      size="text14"
                    >
                      {t('pro.cancelButton')}
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="cancel-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="w-full flex flex-col gap-6"
                  >
                    <textarea
                      id="cancel-reason"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder={t('pro.cancelForm.placeholder')}
                      className={cvaTextarea()}
                    />
                    <div className={cvaButtonsContainer()}>
                      <Button
                        onClick={handleSendCancel}
                        className={cvaSendButton()}
                        variant="outline"
                        borderRadius="3xl"
                        size="semiMedium"
                      >
                        {t('pro.cancelForm.sendButton')}
                      </Button>
                      <Button
                        onClick={handleKeepSubscription}
                        className={cvaLeaveButton()}
                        variant="ghost"
                        borderRadius="3xl"
                        size="semiMedium"
                      >
                        {t('pro.cancelForm.keepButton')}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Background gradients */}
            <div className={cvaGradientBottom()}>
              <div className="absolute inset-[-23.256%]">
                <svg
                  className="block size-full"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 630 630"
                >
                  <g filter="url(#filter0_f_bottom)" opacity="0.24">
                    <circle
                      cx="315"
                      cy="315"
                      fill={showCancelForm ? '#99218D' : '#219979'}
                      r="215"
                    />
                  </g>
                  <defs>
                    <filter
                      colorInterpolationFilters="sRGB"
                      filterUnits="userSpaceOnUse"
                      height="630"
                      id="filter0_f_bottom"
                      width="630"
                      x="0"
                      y="0"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feBlend
                        in="SourceGraphic"
                        in2="BackgroundImageFix"
                        mode="normal"
                        result="shape"
                      />
                      <feGaussianBlur result="effect1_foregroundBlur" stdDeviation="50" />
                    </filter>
                  </defs>
                </svg>
              </div>
            </div>

            <div className={cvaGradientTop()}>
              <div className="absolute inset-[-23.256%]">
                <svg
                  className="block size-full"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 630 630"
                >
                  <g filter="url(#filter0_f_top)" opacity="0.24">
                    <circle
                      cx="315"
                      cy="315"
                      fill={showCancelForm ? '#6F2199' : '#219979'}
                      r="215"
                    />
                  </g>
                  <defs>
                    <filter
                      colorInterpolationFilters="sRGB"
                      filterUnits="userSpaceOnUse"
                      height="630"
                      id="filter0_f_top"
                      width="630"
                      x="0"
                      y="0"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feBlend
                        in="SourceGraphic"
                        in2="BackgroundImageFix"
                        mode="normal"
                        result="shape"
                      />
                      <feGaussianBlur result="effect1_foregroundBlur" stdDeviation="50" />
                    </filter>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  );
};
